import { useEffect } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid2,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import useNotify from '../../hooks/useNotify';
import { useCapabilities } from '../../hooks/useCapabilities';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import CapacityReservationEditor from './CapacityReservationEditor';
import TimeslotPriceMatrix from './TimeslotPriceMatrix';
import * as Yup from 'yup';
import { showsAutocomplete } from '../../autocomplete';
import AutocompleteSelect from '../../components/atom/AutoCompleteSelect';
import { useTranslation } from 'react-i18next';
import {
    formatDateTimeInVenueTz,
    formatDateTimeLocal,
    parseDateTimeLocal,
} from '../../helpers/formatDateTime';
import {
    addTimeslot,
    editTimeslot,
    setTimeslotPrices,
    useGetTimeslot,
} from '@openapi/timeslots/timeslots';
import { formatCancellationReason } from '../../helpers/cancellationReason';
import { useGetTicketTypes } from '@openapi/ticket-types/ticket-types';
import { useGetArrangements } from '@openapi/shows/shows';
import { useSlotTemplate } from '../../hooks/useSlotTemplate';
import { resolveSlotLabel } from '../../helpers/slotTemplate';

function EditTimeslotPage({ newTimeslot }) {
    const { t } = useTranslation();
    const { showId, timeslotId } = useParams();
    const { showModal } = useModals();
    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();
    const { can, CAPABILITIES } = useCapabilities();
    const canFinishCheckin = can(CAPABILITIES.CHECKIN_FINISH);

    const defaultTimeslot = {
        startTime: new Date(),
        isReviewEmailSuppressed: false,
        isHidden: false,
        showId,
        activeArrangementId: null,
    };

    const { data: arrangementsData } = useGetArrangements(Number(showId), {
        query: { enabled: !!showId, onError: notifyError },
    });
    const arrangements = arrangementsData?.arrangements || [];

    const { template, isRentalShow } = useSlotTemplate(showId);

    const { data: ticketTypes, isLoading: loadingTicketTypes } = useGetTicketTypes(
        {
            optionalId: showId,
            onlyShowInList: true,
            includeSeats: true,
        },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    const {
        data,
        isLoading: loading,
        error,
        refetch: reloadTimeslot,
    } = useGetTimeslot(timeslotId, {
        enabled: !!timeslotId && !newTimeslot,
    });

    useEffect(() => {
        if (error) {
            navigate(`/shows/${showId}/timeslots`);
            notifyError(error);
        }
    }, [error, navigate, notifyError, showId]);

    const handleReopenCheckin = () => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    await editTimeslot(timeslotId, {
                        showId: data.showId,
                        startTime: data.startTime,
                        isCheckinClosed: false,
                    });
                    await reloadTimeslot();
                    notifySuccess(t('checkinview.notifications.reopened'));
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    const handleUncancelTimeslot = () => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    await editTimeslot(timeslotId, {
                        showId: parseInt(data.showId, 10),
                        startTime: data.startTime,
                        cancellationReason: null,
                    });
                    await reloadTimeslot();
                    notifySuccess(t('timeslots.notifications.uncancelled'));
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    // Cancellation is enforced server-side off cancellationReason - the booking
    // endpoints refuse a cancelled timeslot - so we only set the reason here and
    // deliberately leave ticket_type_reserved_capacity untouched.
    const handleCancelTimeslot = async (reason) => {
        try {
            await editTimeslot(timeslotId, {
                showId: parseInt(data.showId, 10),
                startTime: data.startTime,
                cancellationReason: reason,
            });
            notifySuccess(t('timeslots.notifications.cancelled'));
            navigate(`/shows/${showId}/timeslots`);
        } catch (err) {
            notifyError(err);
            throw err; // surface the failure and keep the modal open for retry
        }
    };

    const onEdit = (values) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    await editTimeslot(timeslotId, {
                        showId: parseInt(values.showId, 10),
                        startTime: values.startTime,
                        isReviewEmailSuppressed: values.isReviewEmailSuppressed,
                        ticketTypeReservedCapacity: values.ticketTypeReservedCapacity,
                        isHidden: !!values.isHidden,
                        activeArrangementId: values.activeArrangementId ?? null,
                        timeslotGroupId: values.timeslotGroupId
                            ? parseInt(values.timeslotGroupId, 10)
                            : null,
                        slotKey: values.slotKey || null,
                        note: values.note ?? null,
                    });
                    // Persist the per-timeslot price overrides separately so the
                    // dynamic-pricing engine can reuse the same endpoint later.
                    // Only when the matrix has loaded/produced a value — undefined
                    // means the matrix hasn't hydrated yet, and sending [] would
                    // wipe existing overrides.
                    if (values.timeslotPrices !== undefined) {
                        await setTimeslotPrices(Number(timeslotId), {
                            prices: values.timeslotPrices,
                        });
                    }
                    navigate(`/shows/${showId}/timeslots`);
                    notifySuccess(t('timeslots.notifications.updated'));
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    const onSubmit = async (values) => {
        if (!newTimeslot) {
            return onEdit(values);
        }
        try {
            const created = await addTimeslot({
                showId: parseInt(values.showId, 10),
                startTime: values.startTime,
                isReviewEmailSuppressed: values.isReviewEmailSuppressed,
                ticketTypeReservedCapacity: values.ticketTypeReservedCapacity,
                isHidden: !!values.isHidden,
                activeArrangementId: values.activeArrangementId ?? null,
                timeslotGroupId: values.timeslotGroupId
                    ? parseInt(values.timeslotGroupId, 10)
                    : null,
                slotKey: values.slotKey || null,
                durationMinutes: values.slotKey
                    ? (template.find((slot) => slot.key === values.slotKey)?.durationMinutes ?? null)
                    : null,
                note: values.note ?? null,
            });
            if (created?.id && values.timeslotPrices?.length) {
                await setTimeslotPrices(created.id, { prices: values.timeslotPrices });
            }
            navigate(`/shows/${showId}/timeslots`);
            notifySuccess(t('timeslots.notifications.added'));
        } catch (error) {
            notifyError(error);
        }
    };

    if (loading || loadingTicketTypes) {
        return true;
    }

    const validateSchema = Yup.object().shape({
        showId: Yup.number().required(t('common.required')),
        startTime: Yup.string().required(t('common.required')),
    });

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={data || { ...defaultTimeslot }}
            validationSchema={validateSchema}
            enableReinitialize
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => (
                <Form>
                    <PageWithTitle
                        title={
                            newTimeslot
                                ? t('timeslots.newTimeslotName')
                                : `${formatDateTimeInVenueTz(data?.startTime)}`
                        }
                        headline={
                            <Grid2 display={'flex'} flexDirection={'row'} gap={2}>
                                <Grid2>
                                    <CommonButton
                                        fullWidth
                                        onClick={() => navigate(`/shows/${showId}/timeslots`)}
                                        label={t('common.cancel')}
                                        buttonType="secondary"
                                    />
                                </Grid2>
                                <Grid2>
                                    <CommonButton
                                        fullWidth
                                        disabled={isSubmitting}
                                        type="submit"
                                        label={t('common.save')}
                                        buttonType="primary"
                                    />
                                </Grid2>
                            </Grid2>
                        }
                    >
                        {!newTimeslot && data?.isCheckinClosed && (
                            <Grid2 size={{ xs: 12 }}>
                                <Alert
                                    severity="info"
                                    action={
                                        canFinishCheckin && (
                                            <CommonButton
                                                onClick={handleReopenCheckin}
                                                label={t('checkinview.reopenButton')}
                                                buttonType="secondary"
                                            />
                                        )
                                    }
                                >
                                    {t('timeslots.checkinClosedBanner')}
                                </Alert>
                            </Grid2>
                        )}
                        {!newTimeslot && data?.cancellationReason && (
                            <Grid2 size={{ xs: 12 }}>
                                <Alert severity="error">
                                    {t('timeslots.cancelledBanner')}:{' '}
                                    {formatCancellationReason(data.cancellationReason)}
                                </Alert>
                            </Grid2>
                        )}
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <BlockWithTitle title={t('timeslots.title')}>
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        type="datetime-local"
                                        label={t('timeslots.columns.startTime')}
                                        value={
                                            values.startTime
                                                ? formatDateTimeLocal(values.startTime)
                                                : ''
                                        }
                                        onChange={(ev) => {
                                            // Interpret the datetime-local value in the configured venue timezone
                                            setFieldValue(
                                                'startTime',
                                                parseDateTimeLocal(ev.target.value),
                                            );
                                        }}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        error={touched.startTime && Boolean(errors.startTime)}
                                        helperText={touched.startTime ? errors.startTime : ''}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <AutocompleteSelect
                                        initCall={showsAutocomplete.init}
                                        autocompleteCall={showsAutocomplete.search}
                                        value={values?.showId}
                                        setValue={(ev) => setFieldValue('showId', ev)}
                                        disabled={true}
                                    >
                                        <TextField label={t('shows.model')} required />
                                    </AutocompleteSelect>
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        label={t('timeslots.columns.note')}
                                        placeholder={t('timeslots.inlineEdit.notePlaceholder')}
                                        value={values.note || ''}
                                        onChange={(ev) => setFieldValue('note', ev.target.value)}
                                    />
                                </Grid2>
                                <Grid2
                                    size={{ xs: 12 }}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', md: 'row' },
                                        alignItems: { xs: 'flex-start', md: 'center' },
                                        justifyContent: { xs: 'flex-start', md: 'space-between' },
                                        gap: { xs: 1, md: 0 },
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(values.isReviewEmailSuppressed)}
                                                onChange={(ev) =>
                                                    setFieldValue(
                                                        'isReviewEmailSuppressed',
                                                        ev.target.checked,
                                                    )
                                                }
                                            />
                                        }
                                        label={t('timeslots.columns.isReviewEmailSuppressed')}
                                    />
                                    <Tooltip title={t('timeslots.isHiddenHelp')}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={Boolean(values.isHidden)}
                                                    onChange={(ev) =>
                                                        setFieldValue('isHidden', ev.target.checked)
                                                    }
                                                />
                                            }
                                            label={t('timeslots.columns.isHidden')}
                                        />
                                    </Tooltip>
                                    {!newTimeslot &&
                                        can(CAPABILITIES.SHOWS_MANAGE) &&
                                        (data?.cancellationReason ? (
                                            <Button
                                                variant="outlined"
                                                color="warning"
                                                onClick={handleUncancelTimeslot}
                                            >
                                                {t('timeslots.uncancelTimeslotButton')}
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() =>
                                                    showModal(MODAL_TYPE.CANCEL_TIMESLOT, {
                                                        onConfirm: handleCancelTimeslot,
                                                        cancellationReasons:
                                                            data?.cancellationReasons ?? [],
                                                    })
                                                }
                                            >
                                                {t('timeslots.cancelTimeslotButton')}
                                            </Button>
                                        ))}
                                </Grid2>
                                {arrangements.length > 0 && (
                                    <Grid2 size={{ xs: 12 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{t('seats.activeArrangement')}</InputLabel>
                                            <Select
                                                value={values.activeArrangementId ?? ''}
                                                label={t('seats.activeArrangement')}
                                                onChange={(ev) =>
                                                    setFieldValue(
                                                        'activeArrangementId',
                                                        ev.target.value || null,
                                                    )
                                                }
                                            >
                                                <MenuItem value="">
                                                    {t('seats.defaultArrangement')}
                                                </MenuItem>
                                                {arrangements.map((arr) => (
                                                    <MenuItem key={arr.id} value={arr.id}>
                                                        {arr.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid2>
                                )}
                                {isRentalShow && (
                                    <>
                                        <Grid2 size={{ xs: 12 }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>
                                                    {t('timeslots.columns.timeslotPeriod')}
                                                </InputLabel>
                                                <Select
                                                    value={values.slotKey ?? ''}
                                                    label={t('timeslots.columns.timeslotPeriod')}
                                                    onChange={(ev) =>
                                                        setFieldValue(
                                                            'slotKey',
                                                            ev.target.value || null,
                                                        )
                                                    }
                                                >
                                                    <MenuItem value="">
                                                        {t('timeslots.columns.timeslotPeriodNone')}
                                                    </MenuItem>
                                                    {template.map((slot) => (
                                                        <MenuItem key={slot.key} value={slot.key}>
                                                            {resolveSlotLabel(template, slot)}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid2>
                                        <Grid2 size={{ xs: 12 }}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label={t('timeslots.columns.timeslotGroupId')}
                                                value={values.timeslotGroupId ?? ''}
                                                helperText={t(
                                                    'timeslots.columns.timeslotGroupIdHint',
                                                )}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{ readOnly: true }}
                                            />
                                        </Grid2>
                                    </>
                                )}
                            </BlockWithTitle>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <CapacityReservationEditor
                                ticketTypes={ticketTypes.ticketTypes}
                                value={values.ticketTypeReservedCapacity}
                                onChange={(v) => setFieldValue('ticketTypeReservedCapacity', v)}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                            <TimeslotPriceMatrix
                                showId={showId}
                                ticketTypes={ticketTypes.ticketTypes}
                                timeslotId={timeslotId}
                                setFieldValue={setFieldValue}
                            />
                        </Grid2>
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default EditTimeslotPage;
